import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AceptarTrabajoModal = ({ trabajo, isOpen, onClose, onSuccess }) => {
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comentario.trim() || comentario.trim().length < 10) {
      setError('El comentario debe tener al menos 10 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.patch(`${API}/trabajos/${trabajo.id}/aceptar`, {
        comentario: comentario.trim()
      });

      // Success
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al aceptar el trabajo');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setComentario('');
    setError('');
    onClose();
  };

  if (!trabajo) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Aceptar Trabajo Completado
          </DialogTitle>
          <DialogDescription>
            Confirma que el trabajo ha sido completado satisfactoriamente
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Información del Trabajo */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div>
              <span className="font-semibold text-sm text-gray-600">Folio:</span>
              <span className="ml-2 text-sm">{trabajo.folio}</span>
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-600">Título:</span>
              <span className="ml-2 text-sm">{trabajo.titulo}</span>
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-600">Técnico:</span>
              <span className="ml-2 text-sm">{trabajo.tecnico_asignado_nombre || 'No asignado'}</span>
            </div>
          </div>

          {/* Campo de Comentario */}
          <div className="space-y-2">
            <Label htmlFor="comentario">
              Comentario <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="comentario"
              placeholder="Ingresa tu comentario sobre el trabajo realizado (mínimo 10 caracteres)"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={4}
              required
              disabled={loading}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              Caracteres: {comentario.length} / 10 mínimo
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Footer Buttons */}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || comentario.trim().length < 10}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aceptando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Confirmar Aceptación
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AceptarTrabajoModal;
